import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { Mote, Media, MediaEvent, MediaEventType, MediaState, MoteEvent, MediaType, MoteEventType, MediaOptions, ExternalAudioPlayer } from '../models';
import { AudioItemEvent } from '../models';
import { MoteEventService } from '../providers/mote-event.service';

@Injectable({ providedIn: 'root' })
export class MotesMediaPlayerService {
    private mote: Mote = null;
    private media: Media = null;
    public currentEvent: MediaEvent = new MediaEvent(null, null, MediaEventType.INITIAL, MediaState.NONE)
    public usingExternalPlayer: boolean = false;
    private externalPlayerSubject: ReplaySubject<ExternalAudioPlayer> = new ReplaySubject(1);
    private mediaEventSubject: BehaviorSubject<MediaEvent> = new BehaviorSubject(this.currentEvent);
    private moteRoutingSubject: BehaviorSubject<Mote> = new BehaviorSubject(null);
    private audioItemEventSubject: ReplaySubject<AudioItemEvent> = new ReplaySubject(1);


    private lastProgress: number = -1;

    // Indicates how many pct of the media was consumed
    private consumedPct: number = 0;

    public loadingStatus = "";

    constructor(private moteEventService: MoteEventService) { }

    public registerExternalPlayer(externalPlayer: ExternalAudioPlayer) {
        this.usingExternalPlayer = true;
        this.externalPlayerSubject.next(externalPlayer);
    }

    public watchExternalMediaPlayer() {
        return this.externalPlayerSubject.asObservable();
    }

    public mediaLoaded(mote: Mote, media: Media) {
        if(!media) return console.warn("Could not preload media (no media)", media);
        this.mote = mote;
        this.media = media;

        this.currentEvent.update(this.mote, this.media, MediaEventType.STATEUPDATE, MediaState.LOADED, null);
        this.mediaEventSubject.next(this.currentEvent);
    }

    /**
     * Prepare the media player for delayed playback (which has to be initiated by direct user interaction)
     */
    public async preparePlayer(mote: Mote, media: Media): Promise<void> {
        // Start player
        this.startPlayer(mote, media);

        // If audio is not native, wait 100 ms for sound to load
        if(!this.usingExternalPlayer) await new Promise((resolve) => setTimeout(resolve, 100));

        // Pause player
        this.pausePlayer();
    }

    public startPlayer(mote: Mote, media: Media): void {
        if(!media) return console.warn("Could not start player (no media)", media);

        this.mote = mote;
        this.media = media;

        if(this.currentEvent.state == MediaState.VERSION_ERROR && media.type == MediaType.AMBISONIC) {
            // Try to change the type to normal sound
            console.warn("Changing to fallback stereo play of ambisonic file", this.media.sound);
            this.media.type = MediaType.SOUND;
        }

        this.currentEvent.update(this.mote, this.media, MediaEventType.STATEUPDATE, MediaState.PLAYING, null, 0, 0, 0, 0);
        this.mediaEventSubject.next(this.currentEvent);
    }

    public startSimpleVideoPlayer(videoUrl: string): void {
        let fakeMote = new Mote('fake_mote_for_video');

        let videoMedia = new Media({
            type: MediaType.VIDEO,
            video: videoUrl
        }, 'video_media_' + Date.now());

        this.media = videoMedia;
        this.currentEvent.update(fakeMote, this.media, MediaEventType.STATEUPDATE, MediaState.PLAYING);
        this.mediaEventSubject.next(this.currentEvent);
    }

    public showFullscreenImage(mote: Mote, media: Media): void {
        if(!media) return console.warn("Could not show fullscreen image (no media)", media);
        let ev = new MediaEvent(mote, media, MediaEventType.SHOW_IMAGE, MediaState.PLAYING);
        this.mediaEventSubject.next(ev);
    }

    public pausePlayer(): MediaEvent {
        this.currentEvent.update(this.mote, this.media, MediaEventType.STATEUPDATE, MediaState.PAUSED);
        this.mediaEventSubject.next(this.currentEvent);
        return this.mediaEventSubject.getValue();
    }

    public stopPlayer(): MediaEvent {
        this.currentEvent.update(this.mote, this.media, MediaEventType.STATEUPDATE, MediaState.NONE);
        this.mediaEventSubject.next(this.currentEvent);
        return this.mediaEventSubject.getValue();
    }

    public mediaEnded(): MediaEvent {
        this.currentEvent.update(this.mote, this.media, MediaEventType.STATEUPDATE, MediaState.NONE);
        this.mediaEventSubject.next(this.currentEvent);
        // Fire media consumed event if more than 80 pct of the media was consumed
        if(this.mote && this.consumedPct >= 80) {
            let options: MediaOptions = { mediaKey: this.media.key, consumedPct: this.consumedPct }
            this.moteEventService.emitMoteEvent({ mote: this.mote, type: MoteEventType.MEDIA_CONSUMED, options });
        }
        return this.mediaEventSubject.getValue();
    }

    public reportPlayError(): MediaEvent {
        this.currentEvent.update(this.mote, this.media, MediaEventType.PLAY_ERROR, MediaState.NONE);
        this.mediaEventSubject.next(this.currentEvent);
        return this.mediaEventSubject.getValue();
    }

    public reportVersionError(): MediaEvent {
        this.currentEvent.update(this.mote, this.media, MediaEventType.VERSION_ERROR, MediaState.VERSION_ERROR);
        this.mediaEventSubject.next(this.currentEvent);
        return this.mediaEventSubject.getValue();
    }

    public seek(seekSec: number) {
        let ev = new MediaEvent(this.mote, this.media, MediaEventType.SEEK, MediaState.NONE);
        ev.seekSec = seekSec;
        this.mediaEventSubject.next(ev);
    }

    public setTime(seekSec: number) {
        let ev = new MediaEvent(this.mote, this.media, MediaEventType.SET_CURRENT_TIME, MediaState.PLAYING);
        ev.seekSec = seekSec;
        this.mediaEventSubject.next(ev);
    }

    public setLoadingStatus(loadingStatus: string) {
        this.loadingStatus = loadingStatus;
    }

    public updateProgress(progressPct: number, progressSec: number, durationSec?: number): MediaEvent {
        // If sound is playing in external player, we need to get the duration of the sound from that source
        this.currentEvent.update(this.mote, this.media, MediaEventType.PROGRESSUPDATE, this.currentEvent.state, null, progressPct, durationSec);
        this.mediaEventSubject.next(this.currentEvent);
        // Store percentage of media consumed
        this.consumedPct = progressPct;
        // Check for media actions
        this.executeMediaMoteAction(progressSec);
        return this.mediaEventSubject.getValue();
    }

    private executeMediaMoteAction(progressSec: number) {
        if(!this.mote || !this.mote.mediaEventMap) return;

        // Convert floating point sec to round sec
        let progress = Math.round(progressSec);
        // Make sure we only execute action one time pr. update sec
        if(progress == this.lastProgress) return;

        this.lastProgress = progress;

        let ev = this.mote.mediaEventMap[progress];
        //console.log("Execute media action ", this.mote.mediaEventMap, progressSec, ev);
        if(!ev) return;

        // Convert serializable event to a real mote event (for now assume/require that mote comes from the mote containing the playing media)
        let moteEv: MoteEvent = { type: ev.type, options: ev.options, mote: this.mote };
        // Execute action based on media progress
        this.moteEventService.emitMoteEvent(moteEv);
    }

    public updateLoadProgress(loadPct: number, duration: number, bufferPct: number): MediaEvent {
        this.currentEvent.update(this.mote, this.media, MediaEventType.LOAD_PROGRESS, this.currentEvent.state, null, null, duration, bufferPct, loadPct);
        this.mediaEventSubject.next(this.currentEvent);
        return this.mediaEventSubject.getValue();
    }

    public watchMediaEvents(): BehaviorSubject<MediaEvent> {
        return this.mediaEventSubject;
    }

    public watchMoteRouting(): BehaviorSubject<Mote> {
        return this.moteRoutingSubject;
    }

    public watchAudioItemEvents(): Observable<AudioItemEvent> {
        return this.audioItemEventSubject.asObservable();
    }

    public emitAudioItemEvent(ev: AudioItemEvent): void {
        this.audioItemEventSubject.next(ev);
    }

    public getCurrentState(): MediaState {
        return this.currentEvent.state;
    }
}
